import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdLoader } from '@commander-services/gui-components';
import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { DefaultParams, useLocation, useRoute } from 'wouter';
import TableFilters from './TableFilters';
import TableHead from './TableHead';
import TableBody from './TableBody';
import TablePagination from './TablePagination';
import TableRecordAdding from './TableRecordAdding';
import TableSelectColumns from './TableSelectColumns';
import TableRowHeight from './TableRowHeight';
import MultipleActions from './MultipleActions';
import TablePrint from './TablePrint';
import { IDynamicTableFilterState, IDynamicTableState } from '../../interfaces/tables';
import './styles.scss';
import { getCustomerId, getDefaultCustomer } from '../../Services/UserService';
import {
  TABLE_ALARM,
  TABLE_AUTO_REPORT,
  TABLE_CORRECTIONS,
  TABLE_ECO_DRIVE,
  TABLE_GDPR_PROFILES,
  TABLE_GDPR_PROFILES_HISTORY,
  TABLE_GDPR_USER,
  TABLE_GDPR_USER_HISTORY,
  TABLE_IMPORT_TEMPLATE,
  TABLE_ONLINE_MAP_ASSIGN,
  TABLE_REFUELING,
  TABLE_REFUELINGS_IMPORT_RECORDS,
  TABLE_RIDE_POSITIONS,
  TABLE_TRIP_BOOK,
} from '../../Services/TableService';
import { getAllowedMultipleActions } from './TableService';
import {
  deleteDynamicTableFilter,
  IDatepickerSettings,
  ITableFilter,
  setDynamicTableData,
  setDynamicTableFilter,
  setOpenedMenuName,
  setTableDatepickerAtom,
  setTableFilterAtom,
  tablesAtom,
} from '../../store/recoil/TableState';
import JSONValidationService from '../../Services/JSONValidationService';
import AutoReportDataSchema from '../AutoReports/AutoReportDataSchema.json';
import AutoReportSettingsSchema from '../AutoReports/AutoReportSettingsSchema.json';
import RefuelingDataSchema from '../Refuelings/RefuelingDataSchema.json';
import RefuelingSettingsSchema from '../Refuelings/RefuelingSettingsSchema.json';
import CorrectionDataSchema from '../Corrections/CorrectionDataSchema.json';
import CorrectionSettingsSchema from '../Corrections/CorrectionSettingsSchema.json';
import { IVehicles } from '../Vehicles/types';
import { URL_REFUELING } from '../../router/constants';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import { setLoaderAtom } from '../../store/recoil/AppState';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import { selectedVehiclesAtom, vehiclesAtom } from '../../store/recoil/vehicles';
import { activeLanguageAtom } from '../MainMenu/MainMenuState';
import { postRequest } from '../../Services/HttpService';
import { AxiosResponse } from 'axios';

interface IComponentProps {
  buttons?: {
    open: boolean | string[];
    handle?: boolean | string[];
  };
  name: string;
  mustClosed?: boolean;
  specialLinkData?: {
    name: string;
    type: string;
    id: number;
    vehicleId: number;
    vehicleName: string;
    startAddress: string;
    endAddress: string;
  };
  noBorder?: boolean;
  noTab?: boolean;
  dateFormat?: string;
  handleForm?: any;
  openForm?: any;
  updateTable?: boolean | string;
  filter?: ITableFilter | null;
  reportId?: number;
  autoReportId?: number;
  actionsInMenu?: boolean;
  reloadTable?: boolean;
  dragClassname?: string;
  modalClassName?: boolean;
  routerParams?: DefaultParams | null;
  handleLoadedRecords?: (records: Record<string, any>[]) => void;
}

interface IParentState {
  [key: string]: {
    [subkey: string]: any;
  };
}

export const IS_FORCED = 'forced';

function DynamicTable(props: IComponentProps) {
  const location = useLocation();

  const [loader, setLoader] = useRecoilState<boolean>(setLoaderAtom);
  const [tables, setTables] = useRecoilState<IDynamicTableState>(tablesAtom);
  const activeLanguage = useRecoilValue(activeLanguageAtom);
  const [datepickerSettings, setDatepickerSettings] = useRecoilState<IDatepickerSettings | null>(
    setTableDatepickerAtom
  );
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const loadedVehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const [reloadTable, setReloadTable] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState(false);
  const [lastLabelClick, setLastLabelClick] = React.useState<string | null>(null);
  const [parentState, setparentState] = React.useState<IParentState>({
    groupSelect: {},
    simpleSelect: {},
    simpleInput: {},
    suggestion: {},
  });
  const [valueTable, setValueTable] = React.useState<string | null>(null);
  const [currentPage, setcurrentPage] = React.useState<number>(1);
  const [rowsNumber, setRowsNumber] = React.useState<number>(10);
  const [rowsSortingValue, setrowsSortingValue] = React.useState<string | undefined>();
  const [rowsSortingColumns, setRowsSortingColumns] = React.useState<string | undefined>();
  const [prevSelectedVehicle, setPrevSelectedVehicle] = React.useState<number[]>([]);
  const [prevLoadedVehicle, setprevLoadedVehicle] = React.useState<IVehicles>({});
  const [apiSetting, setApiSetting] = React.useState({
    filterConfig:
      props.name === TABLE_GDPR_USER
        ? { page: 1, paging: 10, filter: { customerId: getCustomerId() } }
        : { page: 1, paging: 10 },
    settings: true,
    isClear: false,
  });
  const [tableFilter, setTableFilter] = useRecoilState<ITableFilter | null>(setTableFilterAtom);
  const [, paramsVehicleId] = useRoute(`${URL_REFUELING}/:vehicleId`);
  const query = useUrlQueryParams();
  const dateId = query.get('date');

  const signalControler = React.useMemo(() => {
    return new AbortController();
  }, []);

  const getDescribingEnabledBoolean = (table: any) => {
    const records = table.records;
    let describingEnabled = true;
    if (records) {
      Object.values(records).forEach((i: any) => {
        if (i.describingEnabled === false) {
          describingEnabled = false;
        }
      });
      return describingEnabled;
    }
    return describingEnabled;
  };

  const getSelectedCustomersRows = (records) => {
    const selectedCustomersRows: number[] = [];
    records.forEach((item) => {
      if (!selectedCustomersRows.includes(item.customerId) && item.customerId) {
        selectedCustomersRows.push(item.customerId);
      }
    });
    return selectedCustomersRows;
  };

  const getDataJSONSchema = () => {
    let schema: any = null;
    switch (props.name) {
      case TABLE_AUTO_REPORT:
        schema = AutoReportDataSchema;
        break;
      case TABLE_REFUELING:
        schema = RefuelingDataSchema;
        break;
      case TABLE_CORRECTIONS:
        schema = CorrectionDataSchema;
        break;
      default:
    }
    return schema;
  };

  const getSettingsJSONSchema = () => {
    let schema: any = null;
    switch (props.name) {
      case TABLE_AUTO_REPORT:
        schema = AutoReportSettingsSchema;
        break;
      case TABLE_REFUELING:
        schema = RefuelingSettingsSchema;
        break;
      case TABLE_CORRECTIONS:
        schema = CorrectionSettingsSchema;
        break;
      default:
    }
    return schema;
  };

  const getJSONSchemaDataValidation = (response) => {
    const schema = getDataJSONSchema();
    if (schema) {
      JSONValidationService.validateSchema(schema, response, `TLC Data Schema - ${props.name}`);
    }
  };

  const getJSONSchemaSettingsValidation = (response) => {
    const schema = getSettingsJSONSchema();
    if (schema) {
      JSONValidationService.validateSchema(schema, response, `TLC Settings Schema - ${props.name}`);
    }
  };

  const handleTableData = async (inputs, needsSettings, name: string) => {
    const { mustClosed, specialLinkData, reportId, autoReportId } = props;

    // BE link and table settings

    if (!inputs) return;

    if (
      specialLinkData &&
      specialLinkData.type === 'positions' &&
      specialLinkData.name === 'ride-book' &&
      !inputs.filter
    ) {
      return;
    }

    const link = specialLinkData
      ? `/v1/${specialLinkData.name}/${specialLinkData.id}/${specialLinkData.type}`
      : `/v1/${name}/list-data`;
    let settingsResponse: any = null;
    if (!specialLinkData && needsSettings) {
      settingsResponse = await postRequest(`/v1/${name}/list-settings`, inputs, { showToaster: false, returnAxiosResponse: true });
    }

    // table data
    const response: AxiosResponse | false = await postRequest(link, inputs, { showToaster: false, returnAxiosResponse: true });

    if (response) {
      const responseDataForJsonSchema = { ...response.data, status: response.status };
      getJSONSchemaDataValidation(responseDataForJsonSchema);
      if (response.data && response.status === 200) {
        // preparing data to Redux
        if (settingsResponse && settingsResponse.data) {
          const responseForSettingsJsonSchema = {
            ...settingsResponse.data,
            status: settingsResponse.status,
          };
          getJSONSchemaSettingsValidation(responseForSettingsJsonSchema);
          response.data = Object.assign(response.data, settingsResponse.data);
        } else if (tables[name]) {
          response.data.columns = tables[name].columns;
          response.data.userSettingsKey = tables[name].userSettingsKey;
          response.data.sorting = tables[name].sorting;
          response.data.actions = tables[name].actions;
        }
        delete response.data.status;
        response.data.activeRows = [];
        response.data.chosenCustomers = [];
        if (tables[name] && tables[name].rowHeight) {
          response.data.rowHeight = tables[name].rowHeight;
        }
        if (
          tables[name] &&
          tables[name].paging &&
          tables[name].paging.rowsPerPage > rowsNumber &&
          tables[name].activeRows
        ) {
          response.data.activeRows = tables[name].activeRows;
          if (tables[name].chosenCustomers) {
            response.data.chosenCustomers = tables[name].chosenCustomers;
          }
        }
        if (tables[name] && tables[name].export) {
          response.data.export = tables[name].export;
        }

        // multipleActionsAllowed
        response.data.multipleActionsAllowed = !!getAllowedMultipleActions(
          response.data.actions.multipleActions,
          name,
          response.data.records.map((i) => i.id),
          getSelectedCustomersRows(response.data.records),
          name === TABLE_REFUELINGS_IMPORT_RECORDS ? response.data.records : []
        ).length;

        response.data.noParking = false;
        response.data.openedMenuName = {};
        response.data.details =
          !tables[name] || !tables[name].details ? true : tables[name].details;
        response.data.subdetails =
          !tables[name] || !tables[name].subdetails ? false : tables[name].subdetails;

        // describingEnabled
        const describingEnabled = getDescribingEnabledBoolean(response.data);
        if (!describingEnabled) {
          if (response.data.columns && response.data.columns.address) {
            response.data.columns.address.system = true;
          }
        }

        if (props.handleLoadedRecords) {
          props.handleLoadedRecords(response.data.records);
        }
        return { [name]: response.data };
      }
      return { [name]: [] };
    }
    return { [name]: [] };
  };

  const tableFiltersAndSettings = React.useCallback(
    (filterConfig: any = null, needsSettings = false, isClear = false): any => {
      const { mustClosed, specialLinkData, reportId, autoReportId } = props;
      const name = props.name;
      if (
        mustClosed === true ||
        (mustClosed === false && loadedVehicles && Object.keys(loadedVehicles).length > 0)
      ) {
        let inputs = filterConfig;

        if (!inputs) {
          if (tables[name] && tables[name].paging) {
            const { sorting, columns } = tables[name];
            const sortJSON: any = {};
            const filterJSON: any = {};
            Object.keys(sorting).map((item: any) => {
              sortJSON[`${item}`] = sorting[item];
              return item;
            });
            Object.values(columns)
              .filter(
                (item: any) => item.filter !== null && item.filter && item.filter.active === true
              )
              .map((item: any) => {
                filterJSON[`${item.name}`] =
                  !item.filter.multiple && Array.isArray(item.filter.selectedValue)
                    ? item.filter.selectedValue[0]
                    : item.filter.selectedValue;
              });
            inputs = {
              page: tables[name].paging.currentPage,
              paging: tables[name].paging.rowsPerPage,
              sort: { ...sortJSON },
              filter: { ...filterJSON },
            };
          } else {
            inputs =
              props.name === TABLE_GDPR_USER
                ? { page: 1, paging: 10, filter: { customerId: getCustomerId() } }
                : { page: 1, paging: 10 };
          }
          if (
            paramsVehicleId &&
            paramsVehicleId.vehicleId &&
            selectedVehicles.includes(Number(paramsVehicleId.vehicleId)) &&
            props.name === TABLE_REFUELING
          ) {
            inputs = {
              page: 1,
              paging: 10,
              filter: { licenceNumber: [paramsVehicleId.vehicleId] },
            };
          }
        }

        if (inputs) {
          if (!mustClosed || specialLinkData) {
            inputs.filter = {
              ...inputs.filter,
              vehicleId:
                specialLinkData && specialLinkData.vehicleId
                  ? specialLinkData.vehicleId
                  : selectedVehicles.length === 0
                    ? undefined
                    : selectedVehicles,
            };
          }
          if (!isClear || name === TABLE_ECO_DRIVE) {
            if (reportId) {
              inputs.filter = {
                ...inputs.filter,
                reportId,
              };
            }
            if (autoReportId) {
              inputs.filter = {
                ...inputs.filter,
                autoReportId,
              };
            }
            if (props.filter) {
              inputs.filter = {
                ...inputs.filter,
                ...props.filter,
              };
            }
          }

          if (
            props.filter &&
            props.filter.licenceNumber &&
            (props.name === TABLE_ONLINE_MAP_ASSIGN || props.name === TABLE_CORRECTIONS)
          ) {
            setparentState({
              groupSelect: {},
              simpleSelect: { licenceNumber: { [props.filter.licenceNumber[0]]: true } },
              simpleInput: {},
              suggestion: {},
            });
          }
          if (
            paramsVehicleId &&
            paramsVehicleId.vehicleId &&
            selectedVehicles.includes(Number(paramsVehicleId.vehicleId)) &&
            props.name === TABLE_REFUELING
          ) {
            setparentState({
              groupSelect: {},
              simpleSelect: { licenceNumber: { [paramsVehicleId.vehicleId]: true } },
              simpleInput: {},
              suggestion: {},
            });
          }
          if (
            (props.name === TABLE_GDPR_PROFILES_HISTORY || TABLE_GDPR_USER_HISTORY) &&
            props.filter &&
            props.filter.name
          ) {
            setparentState({
              groupSelect: {},
              simpleSelect: { name: { [props.filter.name[0]]: true } },
              simpleInput: {},
              suggestion: {},
            });
          }

          if (props.name === TABLE_GDPR_USER && inputs.filter && inputs.filter.customerId) {
            if (!getDefaultCustomer().includes(inputs.filter.customerId)) {
              inputs.filter.customerId =
                getCustomerId() && getDefaultCustomer().includes(getCustomerId() as number)
                  ? getCustomerId()
                  : getDefaultCustomer()[0];
            }
          }

          // BE link and table settings

          setApiSetting((currentSetting) => ({
            filterConfig: isEqual(currentSetting.filterConfig, inputs)
              ? currentSetting.filterConfig
              : inputs,
            settings:
              currentSetting.settings !== needsSettings ? needsSettings : currentSetting.settings,
            isClear: currentSetting.isClear === isClear ? currentSetting.isClear : isClear,
          }));

          return { filterConfig: inputs, settings: needsSettings, isClear };
        }
        setApiSetting({ filterConfig, settings: needsSettings, isClear });
      }
      setApiSetting({ filterConfig, settings: needsSettings, isClear });
      return { filterConfig, settings: needsSettings, isClear };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadedVehicles, props, selectedVehicles, tables]
  );

  const actionOpen = (e: any, title: string) => {
    e.preventDefault();
    const { name } = props;
    setSaving(false);
    setOpenMenu(true);
    setTables((oldState: IDynamicTableState) => {
      return setOpenedMenuName(oldState, {
        name,
        title,
        filter: undefined,
        label: true,
      });
    });
  };

  const setValue = (e: any, key: any = null, val: any = null, name: string, columnId: string) => {
    if (val === null && (name === 'simpleInput' || name === 'suggestion')) {
      e.preventDefault();
      const { value } = e.target;

      setparentState({ ...parentState, [name]: { ...parentState[name], [key]: value } });
    } else if (val === null) {
      setparentState({
        ...parentState,
        [name]: {
          ...parentState[name],
          [`start${key.charAt(0).toUpperCase() + key.slice(1)}`]:
            val !== null ? val : e.currentTarget.value,
        },
      });
    } else if (val !== null) {
      setparentState({
        ...parentState,
        [name]: {
          ...parentState[name],
          [columnId]: {
            ...parentState[name][columnId],
            [key]: val !== null ? val : e.currentTarget.checked,
          },
        },
      });
    }
    setLastLabelClick(name);
  };

  const setFilter = (
    name: string,
    columnId: string,
    filter: IDynamicTableFilterState,
    typeFilter: string,
    title: string
  ) => {
    let setDispatch: any = {};
    if (typeFilter !== 'array') {
      setDispatch = {
        name,
        columnId,
        typeFilter,
        title,
        value: filter,
      };
    } else {
      const { max, min } = filter;
      setDispatch = {
        name,
        columnId,
        max,
        min,
        typeFilter,
        title,
      };
    }
    setTables((oldState: IDynamicTableState) => {
      return setDynamicTableFilter(oldState, setDispatch);
    });
  };

  const actionSave = (columnId: string, value: any, e: any, filterType: string, title: string) => {
    const { name } = props;
    const { sorting, paging, columns } = tables[name];

    if (e) {
      e.preventDefault();
    }
    if (
      value !== undefined &&
      value !== null &&
      typeof value === 'object' &&
      typeof value.max === 'object' &&
      value.max
    ) {
      setparentState({
        ...parentState,
        [name]: { startMax: Number(value.max), startMin: Number(value.min) },
      });
    } else {
      setValueTable(value);
    }
    const sortJSON: any = {};
    const filterJSON: any = {};
    Object.keys(sorting).map((item: any) => {
      sortJSON[`${item}`] = sorting[item];
      return item;
    });
    Object.values(columns)
      .filter((item: any) => item.filter !== null && item.filter && item.filter.active === true)
      .map((item: any) => {
        filterJSON[`${item.name}`] =
          !item.filter.multiple && Array.isArray(item.filter.selectedValue)
            ? item.filter.selectedValue[0]
            : item.filter.selectedValue;

        return item;
      });
    const newValue = !columns[columnId].filter.multiple && Array.isArray(value) ? value[0] : value;
    tableFiltersAndSettings(
      {
        page: paging.currentPage,
        paging: paging.rowsPerPage,
        sort: { ...sortJSON },
        filter: { ...filterJSON, [columnId]: newValue },
      },
      true,
      columnId !== 'timestamp'
    );

    setTables((oldState: IDynamicTableState) => {
      return setOpenedMenuName(oldState, {
        name,
        title: columnId,
        filter: null,
        label: true,
      });
    });
    setOpenMenu(false);
    setSaving(true);
    setFilter(name, columnId, value, filterType, title);
  };

  const resetDatepicker = React.useCallback(
    (name: string) => {
      const type = 'date_range_picker';

      setDatepickerSettings({ ...datepickerSettings, [name]: { type, date: [], interval: null } });
    },
    [datepickerSettings, setDatepickerSettings]
  );

  const reset = React.useCallback(
    (columns: any, name: string, isEmpty = false) => {
      if (columns.filter.type !== 'rangeSelect') {
        if (columns.filter.type === 'groupSelect') {
          const newArr: any = {};
          if (columns.filter.selectedValue !== null) {
            Object.values(columns.filter.values).forEach((item: any) => {
              item.forEach((subItem: any) => {
                if (columns.filter.selectedValue.includes(subItem.value)) {
                  newArr[subItem.item] = true;
                }
              });
            });
          }
          setparentState({
            ...parentState,
            [name]: {
              ...parentState[columns.name],
              [columns.name]: isEmpty ? {} : newArr,
            },
          });
        } else if (columns.filter.type === 'simpleSelect') {
          const newArr: any = {};
          if (columns.filter.selectedValue !== null) {
            Object.values(columns.filter.values).forEach((item: any) => {
              if (
                Array.isArray(columns.filter.selectedValue) &&
                columns.filter.selectedValue.includes(String(item.value))
              ) {
                newArr[!item.value ? null : item.value] = true;
              }
            });

            if (
              props.name === TABLE_ONLINE_MAP_ASSIGN &&
              props.filter &&
              props.filter.licenceNumber
            ) {
              setTableFilter(null);
            }
          }

          setparentState({
            ...parentState,
            [columns.filter.type]: {
              ...parentState[columns.filter.type],
              [columns.name]: isEmpty ? {} : newArr,
            },
          });
        } else if (columns.filter.type === 'simpleInput') {
          setparentState({
            ...parentState,
            [columns.filter.type]: { [columns.name]: isEmpty ? '' : columns.filter.selectedValue },
          });
        } else {
          setparentState({
            ...parentState,
            [columns.filter.type]: { [columns.name]: '' },
          });
        }
      } else {
        setparentState({
          ...parentState,
          [name]: {
            startMax: Number(columns.filter.maxValue),
            startMin: Number(columns.filter.minValue),
          },
        });
      }

      setValueTable('');

      // reset datepicker
      if (columns.filter.type === 'dateRangePicker') {
        resetDatepicker(columns.name);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentState, resetDatepicker]
  );

  const clearFilter = React.useCallback(
    (columnId: string) => {
      const { name } = props;
      const { sorting, paging, columns } = tables[name];
      if (tableFilter) {
        setTableFilter((oldState) => {
          const currentState = { ...oldState };
          if (currentState[columnId]) {
            delete currentState[columnId];
          }
          return currentState;
        });
      }

      setTables((oldState: IDynamicTableState) => {
        return deleteDynamicTableFilter(oldState, { name, columnId });
      });

      const sortJSON: any = {};
      const filterJSON: any = {};
      Object.keys(sorting).map((item) => {
        sortJSON[`${item}`] = sorting[item];
        return item;
      });
      Object.values(columns)
        .filter(
          (item: any) =>
            item.name !== columnId && item.filter !== null && item.filter.active === true
        )
        .map((item: any) => {
          filterJSON[`${item.name}`] = item.filter.selectedValue;
          return item;
        });
      tableFiltersAndSettings(
        {
          page: paging.currentPage,
          paging: paging.rowsPerPage,
          sort: { ...sortJSON },
          filter: { ...filterJSON },
        },
        true,
        true
      );
      reset(columns[columnId], name, true);
    },
    [props, reset, setTables, tableFiltersAndSettings, tables]
  );

  const {
    name,
    buttons,
    noBorder,
    noTab,
    dateFormat,
    mustClosed,
    handleForm,
    openForm,
    reportId,
    specialLinkData,
  } = props;
  let vehicleId: any = null;
  if (!mustClosed || specialLinkData) {
    vehicleId =
      specialLinkData && specialLinkData.vehicleId
        ? specialLinkData.vehicleId
        : selectedVehicles.length === 0
          ? undefined
          : selectedVehicles;
  }

  React.useEffect(() => {
    const nameArr = props.name.split('-');
    if (
      props.name &&
      (nameArr.length !== 2 || nameArr[0] !== TABLE_RIDE_POSITIONS) &&
      props.name !== TABLE_ECO_DRIVE
    ) {
      if (!tables || !Object.keys(tables).includes(props.name)) {
        setDatepickerSettings(null);
        setTables({});
      }
    }
    if (
      !tables[props.name] ||
      props.filter?.autoReportId ||
      (props.name === TABLE_GDPR_PROFILES_HISTORY && props.filter?.name) ||
      (props.name === TABLE_GDPR_USER_HISTORY && props.filter?.name)
    ) {
      tableFiltersAndSettings(null, true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.name]);

  React.useEffect(() => {
    if (props.reloadTable) {
      setReloadTable(true);
    }
  }, [props.reloadTable]);

  React.useEffect(() => {
    tableFiltersAndSettings(null, true, false);
    setReloadTable(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomers]);

  React.useEffect(() => {
    if (
      tables[props.name] &&
      tables[props.name].paging &&
      (tables[props.name].paging.currentPage !== currentPage ||
        tables[name].paging.rowsPerPage !== rowsNumber ||
        (Object.values(tables[name].sorting)[0] &&
          typeof Object.values(tables[name].sorting)[0] === 'string' &&
          Object.values(tables[name].sorting)[0] !== rowsSortingValue) ||
        (Object.keys(tables[name].sorting)[0] &&
          typeof Object.keys(tables[name].sorting)[0] === 'string' &&
          Object.keys(tables[name].sorting)[0] !== rowsSortingColumns))
    ) {
      setcurrentPage(tables[props.name].paging.currentPage);
      setRowsNumber(tables[name].paging.rowsPerPage);
      setrowsSortingValue(Object.values(tables[name].sorting)[0] as string);
      setRowsSortingColumns(Object.keys(tables[name].sorting)[0]);
      tableFiltersAndSettings();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, selectedCustomers]);

  React.useEffect(() => {
    if (
      Object.keys(loadedVehicles).length > 0 &&
      props.mustClosed === false &&
      !Object.keys(loadedVehicles).find((findVehicleId: string) =>
        Object.keys(prevLoadedVehicle).includes(findVehicleId)
      )
    ) {
      setprevLoadedVehicle(loadedVehicles);
      tableFiltersAndSettings(null, true);
    }
  }, [loadedVehicles, prevLoadedVehicle, props.mustClosed, tableFiltersAndSettings]);

  React.useEffect(() => {
    if (
      (prevSelectedVehicle && selectedVehicles.length !== prevSelectedVehicle.length) ||
      props.mustClosed === false ||
      activeLanguage ||
      (props.reloadTable && props.reloadTable !== !reloadTable)
    ) {
      setPrevSelectedVehicle(selectedVehicles);

      if (props.reloadTable) {
        setReloadTable(false);
      }

      // if (props.name === 'gdpr/profile' && props.updateTable && reloadTable) {
      //   tableCallApi(null, true, false, 'reloadGdprUser');
      //   if (props.reloadTable) {
      //     setReloadTable(false);
      //   }
      // }

      tableFiltersAndSettings(null, true);

      if (props.filter && props.filter.autoReportId) {
        setparentState({
          groupSelect: {},
          simpleSelect: { autoReportId: { [props.filter.autoReportId[0]]: true } },
          simpleInput: {},
          suggestion: {},
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeLanguage,
    prevSelectedVehicle,
    props.filter,
    props.mustClosed,
    props.name,
    props.reloadTable,
    reloadTable,
    selectedVehicles,
    selectedVehicles.length,
    // tableCallApi,
  ]);

  React.useEffect(() => {
    if (!props.dragClassname) {
      // drag and drop slider
      const slider: any = document.querySelector('.js-table-drag');
      if (slider) {
        let isDown = false;
        let startX: number;
        let scrollLeft: number;
        slider.addEventListener('mousedown', (e: any) => {
          isDown = true;
          startX = e.pageX - slider.offsetLeft;
          scrollLeft = slider.scrollLeft;
        });
        slider.addEventListener('mouseleave', () => {
          isDown = false;
          slider.classList.remove('user-select-off');
        });
        slider.addEventListener('mouseup', () => {
          isDown = false;
          slider.classList.remove('user-select-off');
        });
        slider.addEventListener('mousemove', (e: any) => {
          if (!isDown) {
            return;
          }
          e.preventDefault();
          slider.classList.add('user-select-off');
          const x = e.pageX - slider.offsetLeft;
          const walk = (x - startX) * 3; // scroll-fast
          slider.scrollLeft = scrollLeft - walk;
        });
      }
      const dropdownMenu: any = document.getElementsByClassName('dropdown-menu');
      const modal: any = document.getElementsByClassName('modal');
      const disableEvents = (event: any) => {
        event.stopPropagation();
        return false;
      };
      if (dropdownMenu) {
        for (const item of dropdownMenu) {
          item.addEventListener('mousemove', disableEvents);
        }
      }
      if (modal) {
        for (const item of modal) {
          item.addEventListener('mousemove', disableEvents);
        }
        const elem = document.querySelector('.modal .table-drag');
        if (elem) {
          elem.classList.remove('table-drag');
        }
      }
    }
  });

  React.useEffect(() => {
    if (props.dragClassname) {
      const slider: any = document.querySelector('.drag');

      if (slider) {
        let isDown = false;
        let startX;
        let scrollLeft;

        slider.addEventListener('mousedown', (e) => {
          isDown = true;
          slider.classList.add('active');
          startX = e.pageX - slider.offsetLeft;
          scrollLeft = slider.scrollLeft;
        });
        slider.addEventListener('mouseleave', () => {
          isDown = false;
          slider.classList.remove('active');
        });
        slider.addEventListener('mouseup', () => {
          isDown = false;
          slider.classList.remove('active');
        });
        slider.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          e.preventDefault();
          const x = e.pageX - slider.offsetLeft;
          const walk = (x - startX) * 3; // scroll-fast
          slider.scrollLeft = scrollLeft - walk;
        });
      }
    }
  });

  const handleLoading = (): boolean => {
    switch (props.name) {
      case TABLE_GDPR_PROFILES:
        if (apiSetting.filterConfig && apiSetting.filterConfig) {
          return true;
        }
        return false;
      case TABLE_ALARM:
        if (apiSetting.filterConfig.filter?.timestamp) {
          return false;
        }
        return true;
      case TABLE_IMPORT_TEMPLATE:
        if (apiSetting.filterConfig.filter?.timestamp) {
          return false;
        }
        return true;
      case TABLE_ECO_DRIVE:
        if (apiSetting.filterConfig.filter?.vehicleId) {
          return true;
        }
        return false;

      default:
        return true;
    }
  };

  const {
    isLoading,
    isError,
    data: tableData,
    refetch,
    error,
  } = useQuery({
    queryKey: [
      `${props.name}`,
      apiSetting.filterConfig,
      apiSetting.settings,
      props.name,
      selectedCustomers,
      dateId && datepickerSettings,
    ],
    queryFn: () => handleTableData(apiSetting.filterConfig, apiSetting.settings, props.name),
    staleTime: 9000,
    enabled: handleLoading(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  React.useEffect(() => {
    setTables((oldState: IDynamicTableState) => {
      return setDynamicTableData(oldState, { ...tableData });
    });
  }, [setTables, tableData, apiSetting]);

  const handleReload = () => {
    refetch();
  };

  React.useEffect(() => {
    if (props.updateTable) {
      if (datepickerSettings) {
        const datepickerData = { ...datepickerSettings.timestamp };
        if (
          datepickerData.interval === 'TODAY' ||
          datepickerData.interval === 'LAST_WEEK' ||
          datepickerData.interval === 'THIS_MONTH' ||
          datepickerData.interval === 'THIS_YEAR'
        ) {
          datepickerData.date = [datepickerData.date[0], new Date()];
          setDatepickerSettings({
            ...datepickerSettings,
            timestamp: {
              ...datepickerData,
            },
          });
          if (props.updateTable === IS_FORCED) {
            handleReload();
          }
        } else {
          handleReload();
        }
      } else {
        handleReload();
      }

      if (props.reloadTable) {
        setReloadTable(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.updateTable]);

  return (
    <div
      className={`${
        !noBorder ? 'border-left-on border-right-on border-bottom-on' : ''
      } tab-panel mb-3 fade in active bg-white p-3`}
      style={noTab && !noBorder ? { marginTop: '32px', borderTop: '1px solid #ddd' } : {}}
    >
      {isLoading && <CmdLoader inContent={true} />}
      {tables && Object.keys(tables).length > 0 && tables[name] && tables[name].records && (
        <div>
          <div id="panelElement">
            <div className="row">
              <div className="col-12 col-md-4">
                {buttons && buttons.open && (
                  <TableRecordAdding
                    tableName={name}
                    buttons={buttons}
                    actionSave={tableFiltersAndSettings}
                    isNavigatorDisabled={mustClosed}
                    handleForm={handleForm}
                    reportId={reportId}
                    openForm={openForm}
                  />
                )}
              </div>
              <div className="col-12 col-md-8 text-right">
                {tables[name].export &&
                  tables[name].export.enabled &&
                  ((tables[name].records && tables[name].records.length > 0) ||
                    name === TABLE_TRIP_BOOK) && (
                    <TablePrint
                      sorting={tables[name] && tables[name].sorting ? tables[name].sorting : {}}
                      columns={tables[name] && tables[name].columns ? tables[name].columns : {}}
                      vehicleId={vehicleId}
                      loader={setLoader}
                      actionUrl={tables[name].export.actionUrl}
                      formats={tables[name].export.formats}
                      fileName={tables[name].export.fileName}
                      tableName={name}
                    />
                  )}
                <TableSelectColumns
                  tableName={name}
                  columns={tables[name].columns}
                  userSettingsKey={tables[name].userSettingsKey}
                />
                <TableRowHeight
                  tableName={name}
                  rowHeight={tables[name].rowHeight ? tables[name].rowHeight : false}
                />
              </div>
            </div>
            <TableFilters
              data={tables[name]}
              stateValue={valueTable}
              parentState={parentState}
              actionOpen={actionOpen}
              setValue={setValue}
              // handleChange={this.handleChange}
              actionSave={actionSave}
              reset={reset}
              tableName={name}
              openedMenuName={tables[name].openedMenuName}
              clearFilter={clearFilter}
            />
            {tables[name].activeRows && tables[name].activeRows.length !== 0 && (
              <MultipleActions tableName={name} actionSave={handleReload} />
            )}
          </div>
          <div id="tableWrapper">
            <div
              className={`${
                props.modalClassName ? 'table-responsive b-select-ride-table' : 'table-responsive '
              } ${
                props.dragClassname ? props.dragClassname : ' js-table-drag table-drag'
              } dynamic-table`}
            >
              <table className="table table-striped table-bordered table-listings m-0 border-0">
                <TableHead
                  key={`table-head-${name}`}
                  data={tables[name]}
                  stateValue={valueTable}
                  parentState={parentState}
                  actionOpen={actionOpen}
                  setValue={setValue}
                  actionSave={actionSave}
                  reset={reset}
                  tableName={name}
                  openedMenuName={tables[name].openedMenuName}
                  actionsInMenu={props.actionsInMenu}
                  modalClassName={props.modalClassName}
                />
                <TableBody
                  tableName={name}
                  buttons={buttons}
                  isNavigatorDisabled={mustClosed}
                  data={tables[name]}
                  actionSave={handleReload}
                  dateFormat={dateFormat || 'DD.MM.YYYY HH:mm'}
                  handleForm={handleForm}
                  openForm={openForm}
                  reportName={reportId ? `report_R0${reportId}` : undefined}
                  actionsInMenu={props.actionsInMenu}
                  routerParams={props.routerParams}
                />
              </table>
            </div>
            <TablePagination tableName={name} />
          </div>
        </div>
      )}
    </div>
  );
  // }
}

export default React.memo(DynamicTable);
